/**
 * Desktop implementation of the SDK's `AudioPlayback` boundary.
 * `BrowserAudioPlayback` wraps one browser `Audio` element; Voice Mode and
 * Read Aloud each create their own through their playback queues.
 *
 * Replies play on the default output device, so during a recorded meeting the
 * meeting capture picks them up like any other speaker audio and they can
 * appear in the notes. That is deliberate: a voice reply played into a
 * meeting is part of the meeting.
 */
import { makeDevEventLog } from '@/utils/dev-event-log';

import type { AudioPlayback, PlayableAudio } from '@littlebird/sdk';

/**
 * Development log exposed as `window.__littlebirdResponseAudioLog`. This file
 * logs `play-inaudible` and `play-wedged`; the SDK's `ResponseAudioQueue`
 * logs `play-start`/`play-ended`/`play-error` through this same function via
 * its `debug` option. A `play-start` with no `play-ended` or `play-error`
 * means the browser never finished that piece.
 */
export const logResponseAudioEvent = makeDevEventLog(
  '[voice-response-audio]',
  '__littlebirdResponseAudioLog',
  300
);

/**
 * A playing element emits `timeupdate` several times per second. This much
 * silence mid-piece means the element wedged: it will emit neither `ended`
 * nor `error`, so only a timeout can detect it. Never-starting playback is
 * the same silence, so this also bounds the wait for the first tick.
 */
const PLAYBACK_WEDGE_TIMEOUT_MS = 5_000;

/** Play at most one encoded audio piece; starting another stops the current piece */
export class BrowserAudioPlayback implements AudioPlayback {
  /** Stop callback belonging to the one browser element currently allowed to settle */
  private stopCurrent: (() => void) | null = null;

  /**
   * Start one browser audio element and resolve when it ends or is stopped.
   *
   * Stopping resolves instead of rejecting so queue shutdown does not look like
   * a playback failure.
   */
  play({ mimeType, audioBase64 }: PlayableAudio): Promise<void> {
    this.stop();
    const audio = new Audio(`data:${mimeType};base64,${audioBase64}`);
    // Muted elements are expected only under the live-test harness
    // (`scripts/voice-mode-live-test.cjs`); elsewhere this entry signals a real problem
    if (audio.volume === 0 || audio.muted) {
      logResponseAudioEvent('play-inaudible', {
        volume: audio.volume,
        muted: audio.muted,
      });
    }
    return new Promise((resolve, reject) => {
      let wedgeTimer: ReturnType<typeof setTimeout> | undefined;
      // Only the current element may settle this player; `play` can replace an
      // older element before its browser events arrive
      const settle = (callback: () => void) => {
        if (this.stopCurrent !== handleStop) return;
        clearTimeout(wedgeTimer);
        audio.removeEventListener('ended', handleEnded);
        audio.removeEventListener('error', handleError);
        audio.removeEventListener('timeupdate', restartWedgeTimer);
        this.stopCurrent = null;
        callback();
      };
      const handleEnded = () => settle(resolve);
      const handleError = () =>
        settle(() => reject(new Error('Audio playback failed')));
      const handleStop = () => {
        // Resolve, not reject: an intentional stop (queue shutdown or a
        // discarded reply) is not a playback failure
        audio.pause();
        audio.src = '';
        settle(resolve);
      };
      const handleWedged = () => {
        logResponseAudioEvent('play-wedged', {
          currentTime: audio.currentTime,
        });
        audio.pause();
        audio.src = '';
        settle(() =>
          reject(new Error('Audio playback stopped making progress'))
        );
      };
      const restartWedgeTimer = () => {
        clearTimeout(wedgeTimer);
        wedgeTimer = setTimeout(handleWedged, PLAYBACK_WEDGE_TIMEOUT_MS);
      };

      this.stopCurrent = handleStop;
      audio.addEventListener('ended', handleEnded);
      audio.addEventListener('error', handleError);
      audio.addEventListener('timeupdate', restartWedgeTimer);
      restartWedgeTimer();
      void audio.play().catch((error) => settle(() => reject(error)));
    });
  }

  /** Stop the current element and settle its `play` promise normally */
  stop(): void {
    this.stopCurrent?.();
  }
}
